/*
 * Sticky add to cart bar.
 *
 * Appears once the shopper has left the first screen behind and, by default,
 * gets out of the way again whenever the real add to cart button is on screen —
 * two identical buttons competing for the same tap is worse than none.
 *
 * The bar never owns a cart request of its own. Its add to cart action clicks
 * the page's real submit button, so everything wired to that button — the press
 * cooldown, replace-cart, skip-to-checkout, the cart drawer — happens exactly
 * once and exactly as configured, with no second code path to keep in sync.
 */
(function () {
  if (window.__vrnStickyAtc) return;
  window.__vrnStickyAtc = true;

  if (!window.customElements || window.customElements.get('sticky-atc')) return;

  var ATC_SELECTOR = '[data-atc-button], .product-form__submit';

  /*
   * Same glide the section CTAs use (hero_1 and friends): easeInOutQuad, a
   * duration that scales with distance inside a 350–700ms band, and the target's
   * top parked a fixed offset below the viewport. Copied rather than shared
   * because those scripts are inlined per section and also resolve "the next
   * section", which the bar has no use for — but the curve is identical, so a
   * tap on the bar feels like a tap on any other button in the theme.
   */
  function glideTo(target, offset) {
    var startY = window.pageYOffset;
    var dist = target.getBoundingClientRect().top - offset;
    if (Math.abs(dist) < 2) return;

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo(0, startY + dist);
      return;
    }

    var dur = Math.min(700, Math.max(350, Math.abs(dist) * 0.5));
    var t0 = null;
    function ease(x) {
      return x < 0.5 ? 2 * x * x : -1 + (4 - 2 * x) * x;
    }
    function step(ts) {
      if (!t0) t0 = ts;
      var pr = Math.min(1, (ts - t0) / dur);
      window.scrollTo(0, startY + dist * ease(pr));
      if (pr < 1) window.requestAnimationFrame(step);
    }
    window.requestAnimationFrame(step);
  }

  function realButton() {
    var nodes = document.querySelectorAll(ATC_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      /* Skip our own proxy and anything inside a hidden quick-add drawer. */
      if (nodes[i].closest('sticky-atc')) continue;
      if (nodes[i].offsetParent === null && nodes[i].getClientRects().length === 0) continue;
      return nodes[i];
    }
    return null;
  }

  window.customElements.define(
    'sticky-atc',
    class extends HTMLElement {
      connectedCallback() {
        if (this.__init) return;
        this.__init = true;

        this.button = this.querySelector('[data-satc-button]');
        this.visible = false;
        this.pinned = false; /* theme editor override */
        this.gateOpen = false; /* shopper has scrolled past the trigger */
        this.atcOnScreen = false;

        this.mirrorState();
        this.bindAction();
        this.watchTrigger();
        this.watchRealButton();
        this.bindEditor();
      }

      disconnectedCallback() {
        if (this.triggerObserver) this.triggerObserver.disconnect();
        if (this.atcObserver) this.atcObserver.disconnect();
        if (this.stateObserver) this.stateObserver.disconnect();
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      }

      /* ------------------------------------------------------------------ */

      /*
       * An empty label means "say whatever the real button says", which keeps
       * the two in step through variant changes without a second setting. A
       * disabled or sold-out product has to be reflected too, or the bar offers
       * a purchase the page cannot honour.
       */
      mirrorState() {
        if (this.dataset.action !== 'add_to_cart' || !this.button) return;

        var real = realButton();
        if (!real) return;

        this.label = this.button.querySelector('[data-satc-label]');
        this.customLabel = this.label ? this.label.textContent.trim() : '';

        this.sync(real);

        /* Picking a different variant re-renders the real button — sold out on
           one size and in stock on another has to reach the bar, or it offers a
           purchase the page will refuse. */
        if (!window.MutationObserver) return;
        this.stateObserver = new MutationObserver(
          function () {
            var current = realButton();
            if (current) this.sync(current);
          }.bind(this)
        );
        this.stateObserver.observe(real, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
        });
      }

      sync(real) {
        var realText = (real.querySelector('span') || real).textContent.trim();
        var disabled = real.hasAttribute('disabled');
        var mirror = this.dataset.mirrorState === 'true';

        if (this.label) {
          /* An empty label in the markup means "say what the real button says".
             A merchant-set label is kept — except while sold out, where the
             truth matters more than the wording. */
          if (!this.customLabel) this.label.textContent = realText || real.dataset.atcLabel || '';
          else if (mirror && disabled) this.label.textContent = realText;
          else this.label.textContent = this.customLabel;
        }

        if (!mirror) return;

        if (disabled) {
          this.button.setAttribute('disabled', '');
          this.button.setAttribute('aria-disabled', 'true');
        } else {
          this.button.removeAttribute('disabled');
          this.button.removeAttribute('aria-disabled');
        }
      }

      bindAction() {
        if (!this.button) return;

        if (this.dataset.action === 'scroll') {
          var offset = parseInt(this.dataset.scrollOffset, 10);
          if (isNaN(offset)) offset = 100;

          this.button.addEventListener('click', function (event) {
            var href = this.getAttribute('href') || '';
            if (href.charAt(0) !== '#' || href === '#') return;

            var target;
            try {
              target = document.querySelector(href);
            } catch (err) {
              return;
            }
            if (!target) return;

            /* Only take over once there is somewhere to go — otherwise let the
               browser fall back to its own anchor jump. */
            event.preventDefault();
            glideTo(target, offset);
          });
          return;
        }

        if (this.dataset.action !== 'add_to_cart') return;

        this.button.addEventListener('click', function (event) {
          event.preventDefault();

          var real = realButton();
          if (!real || real.hasAttribute('disabled')) return;

          /* Hand the press straight to the real button: one cart request, one
             set of rules, and any validation error surfaces in its own form
             where the shopper's next action already lives. */
          real.click();
        });
      }

      /* ------------------------------------------------------------------ */

      watchTrigger() {
        var mode = this.dataset.trigger || 'first_section';

        if (mode === 'immediate') {
          this.gateOpen = true;
          this.render();
          return;
        }

        if (mode === 'depth') {
          var pct = parseInt(this.dataset.depth, 10);
          if (isNaN(pct)) pct = 25;
          this.onScroll = function () {
            var max = document.documentElement.scrollHeight - window.innerHeight;
            var done = max > 0 ? (window.pageYOffset / max) * 100 : 100;
            var open = done >= pct;
            if (open !== this.gateOpen) {
              this.gateOpen = open;
              this.render();
            }
          }.bind(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onScroll();
          return;
        }

        var target = this.findTarget(mode);
        if (!target) {
          /* Nothing to watch means nothing to wait for — showing the bar beats
             hiding it forever because a selector was mistyped. */
          this.gateOpen = true;
          this.render();
          return;
        }

        if (!('IntersectionObserver' in window)) {
          this.gateOpen = true;
          this.render();
          return;
        }

        this.triggerObserver = new IntersectionObserver(
          function (entries) {
            var e = entries[0];
            /* Only count leaving upwards. Scrolling back above the hero (a
               bounce on iOS, an anchor jump) should not pop the bar open. */
            var passed = !e.isIntersecting && e.boundingClientRect.bottom <= 0;
            if (passed !== this.gateOpen) {
              this.gateOpen = passed;
              this.render();
            }
          }.bind(this),
          { threshold: 0 }
        );
        this.triggerObserver.observe(target);
      }

      findTarget(mode) {
        if (mode === 'selector') {
          var sel = (this.dataset.selector || '').trim();
          if (!sel) return null;
          try {
            return document.querySelector(sel);
          } catch (err) {
            return null;
          }
        }

        /* First section of the template — the above-the-fold block. The bar's
           own section is skipped, and so is anything with no height (an empty
           app embed, a section the merchant hid). */
        var own = this.closest('.shopify-section');
        var sections = document.querySelectorAll('#MainContent > .shopify-section, #MainContent .shopify-section');
        for (var i = 0; i < sections.length; i++) {
          if (sections[i] === own || sections[i].contains(this)) continue;
          if (sections[i].offsetHeight < 40) continue;
          return sections[i];
        }
        return null;
      }

      watchRealButton() {
        if (this.dataset.hideWhenAtcVisible !== 'true') return;
        if (!('IntersectionObserver' in window)) return;

        var real = realButton();
        if (!real) return;

        this.atcObserver = new IntersectionObserver(
          function (entries) {
            var on = entries[0].isIntersecting;
            if (on !== this.atcOnScreen) {
              this.atcOnScreen = on;
              this.render();
            }
          }.bind(this),
          { threshold: 0 }
        );
        this.atcObserver.observe(real);
      }

      /* ------------------------------------------------------------------ */

      bindEditor() {
        if (!window.Shopify || !window.Shopify.designMode) return;

        var id = this.dataset.sectionId;
        document.addEventListener(
          'shopify:section:select',
          function (event) {
            if (event.detail.sectionId !== id) return;
            this.pinned = true;
            this.render();
          }.bind(this)
        );
        document.addEventListener(
          'shopify:section:deselect',
          function (event) {
            if (event.detail.sectionId !== id) return;
            this.pinned = false;
            this.render();
          }.bind(this)
        );
      }

      render() {
        var next = this.pinned || (this.gateOpen && !this.atcOnScreen);
        if (next === this.visible) return;
        this.visible = next;
        this.classList.toggle('is-visible', next);
        this.setAttribute('aria-hidden', next ? 'false' : 'true');
      }
    }
  );
})();
